import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Popper,
  Fade,
  Card as MuiCard,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import themes from '../theme/themes';

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentTheme, changeTheme, allThemes } = useTheme();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPalette, setOpenPalette] = useState(false);

  const fetchRides = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/rides/user/${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRides(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingRides'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    fetchRides();
  }, [fetchRides]);

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPalette((prev) => !prev);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setOpenPalette(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'info';
      case 'started': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('pending');
      case 'accepted': return t('accepted');
      case 'started': return t('started');
      case 'completed': return t('completed');
      case 'cancelled': return t('cancelled');
      default: return status;
    }
  };

  return (
    <Box sx={{
      pb: 8,
      bgcolor: (theme) => theme.palette.background.default,
      minHeight: '100vh',
      transition: 'background 0.5s ease',
    }}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          bgcolor: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(16px)',
          borderBottom: (theme) => `2px solid ${theme.palette.primary.light}40`,
          transition: 'all 0.5s ease',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            🚗 {t('appName')}
          </Typography>

          {/* زر البروفايل */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonIcon />}
            onClick={() => navigate('/profile')}
            sx={{
              borderRadius: 50,
              mx: 1,
              borderColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.main,
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.dark,
                background: (theme) => `${theme.palette.primary.main}20`,
              },
            }}
          >
            {t('profile') || 'الملف الشخصي'}
          </Button>

          {/* زر تبديل الثيمات */}
          <Tooltip title={t('changeTheme') || 'تغيير الثيم'}>
            <IconButton
              onClick={handlePaletteClick}
              sx={{
                mx: 1,
                color: (theme) => theme.palette.primary.main,
                '&:hover': {
                  background: (theme) => `${theme.palette.primary.main}20`,
                },
              }}
            >
              <PaletteIcon />
            </IconButton>
          </Tooltip>

          {/* قائمة الثيمات المنبثقة */}
          <Popper
            open={openPalette}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            sx={{ zIndex: 9999 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <MuiCard
                  sx={{
                    p: 2,
                    mt: 1,
                    borderRadius: 3,
                    maxWidth: 280,
                    background: (theme) => theme.palette.background.paper,
                    backdropFilter: 'blur(20px)',
                    border: (theme) => `1px solid ${theme.palette.primary.light}40`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    🎨 اختر الثيم المفضل
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {Object.values(allThemes).map((themeData) => (
                      <Button
                        key={themeData.id}
                        variant={currentTheme === themeData.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleThemeChange(themeData.id)}
                        sx={{
                          justifyContent: 'flex-start',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: (theme) => theme.palette.primary.main,
                          color: currentTheme === themeData.id ? '#FFFFFF' : (theme) => theme.palette.text.primary,
                          background: currentTheme === themeData.id
                            ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                            : 'transparent',
                          '&:hover': {
                            background: currentTheme === themeData.id
                              ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                              : (theme) => `${themeData.colors.primary}20`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            mr: 1.5,
                            background: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`,
                            border: '2px solid rgba(255,255,255,0.3)',
                          }}
                        />
                        <span dir="ltr">{themeData.icon} {themeData.name}</span>
                      </Button>
                    ))}
                  </Box>
                </MuiCard>
              </Fade>
            )}
          </Popper>

          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              mx: 1,
              '& .MuiToggleButton-root': {
                borderRadius: 50,
                px: 2,
                borderColor: (theme) => theme.palette.primary.light,
                '&.Mui-selected': {
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <ToggleButton value="ar">🇸🇦</ToggleButton>
            <ToggleButton value="en">🇬🇧</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            onClick={logout}
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: 50,
              borderColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.main,
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.dark,
                background: (theme) => `${theme.palette.primary.main}20`,
              },
            }}
          >
            {t('logout')}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {/* بطاقة المستخدم */}
        <Card
          sx={{
            p: 3,
            mb: 3,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
            border: (theme) => `1px solid ${theme.palette.primary.light}40`,
            transition: 'all 0.5s ease',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600} color="text.primary">
                  {t('welcome')} 👋 {user?.name}
                </Typography>
                <Chip
                  label={user?.role === 'driver' ? t('captain') : t('passenger')}
                  size="small"
                  sx={{
                    mt: 1,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: '#FFFFFF',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('balance')}
              </Typography>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {user?.balance || 0} {t('riyal')}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* أزرار الإجراءات السريعة - حسب دور المستخدم */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {user?.role === 'passenger' && (
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<LocationIcon />}
                onClick={() => navigate('/request-ride')}
                sx={{ py: 1.5, borderRadius: 3 }}
              >
                {t('requestRide')}
              </Button>
            </Grid>
          )}
          {user?.role === 'driver' && (
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CarIcon />}
                onClick={() => navigate('/captain')}
                sx={{ 
                  py: 1.5, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #4A00E0, #7B2FBE)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2E005E, #4A00E0)',
                  }
                }}
              >
                {t('captainDashboard') || 'لوحة الكابتن'}
              </Button>
            </Grid>
          )}
          <Grid item xs={user?.role === 'passenger' ? 6 : 12}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={fetchRides}
              sx={{
                py: 1.5,
                borderRadius: 3,
                borderColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.main,
                '&:hover': {
                  borderColor: (theme) => theme.palette.primary.dark,
                  background: (theme) => `${theme.palette.primary.main}20`,
                },
              }}
            >
              {t('myRides')}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, borderColor: (theme) => `${theme.palette.primary.light}40` }} />

        {/* قائمة الرحلات */}
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: (theme) => theme.palette.secondary.main,
          }}
        >
          {t('myRides')}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(211, 47, 47, 0.2)',
            }}
          >
            {error}
          </Alert>
        ) : rides.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: (theme) => `1px dashed ${theme.palette.primary.light}40`,
              background: 'transparent',
            }}
          >
            <Typography color="text.secondary">{t('noRides')}</Typography>
          </Paper>
        ) : (
          rides.map((ride) => (
            <Card
              key={ride.id}
              sx={{
                mb: 2,
                borderRadius: 3,
                borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: (theme) => `0 8px 32px ${theme.palette.primary.main}20`,
                  transform: 'translateX(4px)',
                },
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                      {t('rideDetails')} #{ride.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ride.pickup_address} → {ride.dropoff_address}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: (theme) => theme.palette.primary.main, fontWeight: 600 }}>
                      {t('estimatedFare')}: {ride.estimated_fare} {t('riyal')}
                    </Typography>
                  </Box>
                  <Chip
                    label={getStatusText(ride.status)}
                    color={getStatusColor(ride.status)}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box display="flex" gap={1} mt={2}>
                  {ride.status === 'accepted' && (
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ borderRadius: 50 }}
                    >
                      {t('start')}
                    </Button>
                  )}
                  {ride.status === 'started' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                    >
                      {t('complete')}
                    </Button>
                  )}
                  {(ride.status === 'pending' || ride.status === 'accepted') && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      sx={{ borderRadius: 50 }}
                    >
                      {t('cancel')}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Container>
    </Box>
  );
};

export default DashboardPage;